import { useId, useState } from 'react';
import Modal from '../Modal';
import MultiSearchableSelect from '../MultiSearchableSelect';
import type { PlanGroupConfig, StoreFieldKey } from '../../types/preJourneyPlan';
import {
  GROUP_NAME_MAX,
  clip,
  fieldLabel,
  nextId,
  placeGroup,
  unclaimedValues,
  valueLabel,
  valueOptions,
  type FieldMap,
} from './preJourneyPlanConfig';

/**
 * Store Visits Configuration › Pre-Journey Plan › a store group's values.
 *
 * Edit values (merge): the values a group covers, one picker per group field. Values another group holds
 * move here; a group whose values all move is merged in (after asking, when it has
 * priorities). Add store group: a new group for values no group holds yet. Apply hands the
 * groups back to the tab; nothing is saved until the tab's Save.
 */

export const DISCARD_MERGED_PROMPT = 'Merging drops the priorities of the groups taken in. Go on?';

interface Props {
  /** The group whose values are edited; left out, a new group is added. */
  group?: PlanGroupConfig;
  groupBy: StoreFieldKey[];
  groups: PlanGroupConfig[];
  fields: FieldMap;
  onClose: () => void;
  onApply: (groups: PlanGroupConfig[]) => void;
}

export default function PreJourneyGroupValuesModal({ group, groupBy, groups, fields, onClose, onApply }: Props) {
  const id = useId();
  const adding = !group;
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[][]>(() => groupBy.map((_, i) => [...(group?.match[i] ?? [])]));
  const [error, setError] = useState<string | null>(null);

  const optionsFor = (key: StoreFieldKey, dim: number): string[] => (adding && groupBy.length === 1
    // One field: a new group can only take what no group holds.
    ? unclaimedValues(fields, key, groups, dim)
    : valueOptions(fields, key, groups, dim));

  const apply = () => {
    const target: PlanGroupConfig = group ?? {
      id: nextId('g', groups.map((g) => g.id)),
      name: clip(name.trim() || picked.map((list, i) => list.map((v) => valueLabel(groupBy[i], v)).join(', ')).join(' · '), GROUP_NAME_MAX),
      enabled: true,
      match: [],
      priorities: [],
    };
    const placed = placeGroup(groups, target, picked, adding ? 'add' : 'merge');
    if ('error' in placed) {
      setError(placed.error);
      return;
    }
    if (placed.removed.some((g) => g.priorities.length > 0) && !window.confirm(DISCARD_MERGED_PROMPT)) return;
    onApply(placed.groups);
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={group ? `Edit values · ${group.name.trim() || 'Store group'}` : 'Add store group'}
      size="md"
      footer={(
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
          >
            {adding ? 'Add' : 'Apply'}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        {adding && (
          <div>
            <label htmlFor={`${id}-name`} className="text-sm font-semibold text-gray-900">Name</label>
            <input
              id={`${id}-name`}
              type="text"
              maxLength={GROUP_NAME_MAX}
              value={name}
              placeholder="From the values"
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        )}
        {groupBy.map((key, dim) => (
          <div key={key}>
            <label htmlFor={`${id}-f${dim}`} className="text-sm font-semibold text-gray-900">{fieldLabel(fields, key)}</label>
            <div className="mt-1">
              <MultiSearchableSelect
                id={`${id}-f${dim}`}
                values={picked[dim] ?? []}
                onChange={(values) => {
                  setPicked((cur) => cur.map((list, i) => (i === dim ? values : list)));
                  setError(null);
                }}
                options={optionsFor(key, dim).map((v) => ({ value: v, label: valueLabel(key, v) }))}
                placeholder="Pick values"
                emptyText={adding ? 'Every value is in a group' : 'No matches'}
              />
            </div>
          </div>
        ))}
        {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
      </div>
    </Modal>
  );
}
